import React, { useState } from 'react';
import { Building2, ArrowLeft, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { $api } from '../../api';
import { useOrganization } from '../../contexts/OrganizationContext';
import ErrorMessage from '../ErrorMessage';
import SuccessMessage from '../SuccessMessage';

const CreateOrganization: React.FC = () => {
  const navigate = useNavigate();
  const { refreshOrganizations, switchOrganization, currentOrganization } = useOrganization();
  const [name, setName] = useState('');
  const [success, setSuccess] = useState<string | null>(null);

  // useMutation for creating organization
  const createOrgMutation = $api.useMutation('post', '/api/organizations');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(null);

    createOrgMutation.mutate(
      {
        body: { name },
      },
      {
        onSuccess: async (data) => {
          setSuccess(data.message || 'Organisation créée avec succès');
          
          // Refresh organizations list
          const orgs = await refreshOrganizations();
          
          // Switch to the new organization
          if (data.organization_id) {
            setTimeout(() => {
              switchOrganization(data.organization_id, '/dashboard', orgs);
            }, 1500);
          }
        },
      }
    );
  };

  return (
    <div className="p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        { currentOrganization && <button
          onClick={() => navigate('/organizations')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm">Retour aux organisations</span>
        </button> }

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {/* Icon and Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
              <Building2 className="h-8 w-8 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Créer une Nouvelle Organisation
            </h2>
            <p className="text-gray-600">
              Une organisation vous permet de gérer des entités et de collaborer avec votre équipe
            </p>
          </div>

          {createOrgMutation.error && (
            <ErrorMessage error={createOrgMutation.error} className="mb-6" />
          )}

          {success && (
            <SuccessMessage message={success} className="mb-6" />
          )}

          {!success && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de l'organisation *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Mon Entreprise SAS"
                  required
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-2">
                  Le nom de votre entreprise ou organisation
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">En tant que propriétaire, vous pourrez :</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li className="flex items-center space-x-2">
                    <span className="h-1.5 w-1.5 bg-blue-600 rounded-full"></span>
                    <span>Gérer les entités (personnes et entreprises)</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="h-1.5 w-1.5 bg-blue-600 rounded-full"></span>
                    <span>Inviter des membres et administrateurs</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="h-1.5 w-1.5 bg-blue-600 rounded-full"></span>
                    <span>Effectuer des screenings KYC</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="h-1.5 w-1.5 bg-blue-600 rounded-full"></span>
                    <span>Consulter la facturation et les statistiques</span>
                  </li>
                </ul>
              </div>

              <button
                type="submit"
                disabled={createOrgMutation.isPending}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createOrgMutation.isPending ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Création en cours...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <Plus className="h-5 w-5" />
                    <span>Créer l'organisation</span>
                  </div>
                )}
              </button>
            </form>
          )}

          {success && (
            <div className="text-center">
              <p className="text-gray-600 mb-6">
                Redirection vers votre nouvelle organisation...
              </p>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateOrganization;

